/**
 * RecordTasksListTab — wraps RecordObjectListTab with task-specific config and
 * an "Add task" control in the list toolbar's trailing slot.
 *
 * Deliberately the same shape as RecordNotesListTab (the record's
 * "Notes" tab): same embedded list, same toolbar placement for the
 * create action, same empty-state wiring via `onCreateClick`. A task's stage is
 * already a column, and the list's own filter bar covers the rest, so the
 * trailing slot holds the create button alone.
 */

import { Plus } from '@untitledui/icons';
import { useMemo } from 'react';

import { Button } from '@/lib/base/buttons/button';

import { taskListConfig } from '../../list/config/taskListConfig';
import { useTaskTabActions } from '../task/useTaskTabActions';
import { COMPACT_TOOLBAR_BUTTON_CLASS } from './HeaderActions';
import { RecordObjectListTab } from './RecordObjectListTab';

// Matches RecordNotesListTab so the two tabs' create actions sit identically.
const COMPACT_CREATE_BUTTON_CLASS = `${COMPACT_TOOLBAR_BUTTON_CLASS} !px-2 !text-xs`;

interface RecordTasksListTabProps {
  entityId: string;
  /** The host record's entity_type_name, passed to the composer. */
  hostEntityType: string;
  countLabel?: string;
  countLabelSingular?: string;
}

export function RecordTasksListTab({
  entityId,
  hostEntityType,
  countLabel,
  countLabelSingular,
}: RecordTasksListTabProps) {
  const { startCreatingTask, canCreateTask, isStageLoading } =
    useTaskTabActions(entityId, hostEntityType);

  const toolbarTrailing = useMemo(
    () =>
      canCreateTask ? (
        <Button
          size='sm'
          color='primary'
          iconLeading={Plus}
          onClick={startCreatingTask}
          aria-label='Add task'
          className={COMPACT_CREATE_BUTTON_CLASS}
          isDisabled={isStageLoading}
          disabledTooltip='Loading…'
        >
          Add task
        </Button>
      ) : undefined,
    [canCreateTask, startCreatingTask, isStageLoading]
  );

  return (
    <RecordObjectListTab
      baseConfig={taskListConfig}
      entityId={entityId}
      hostEntityType={hostEntityType}
      countLabel={countLabel}
      countLabelSingular={countLabelSingular}
      onCreateClick={startCreatingTask}
      createDisabled={isStageLoading}
      createDisabledTooltip='Loading…'
      toolbarTrailing={toolbarTrailing}
    />
  );
}
